/**
 * The Overview of a team, a member of a team or a specialist (AIOE 0.90.0, item 1). AIOE's own contract: the console
 * reads it; the workbench may read it later, and would copy this file whole.
 *
 *   GET /teams/v1/:teamId/overview?range=30d                      a team, or a specialist (a record of kind `specialist`)
 *   GET /teams/v1/:teamId/members/:member/overview?range=30d      one member of a team, by its name or its id
 *       range: 1h | 1d | 7d | 30d | 60d | 90d (default 30d), or `since` and `until` (ISO) for a range of one's own
 *
 * Who may read it: whoever may read the team (routes/teamCheck.ts `reads`: a portfolio or business-area manager, anyone
 * with a role in a project it is granted to or was defined in), its owner, and an administrator. Another organisation's
 * team, and one the caller may not read, is 404. An administrator, a portfolio manager and the team's owner see its work
 * in every project; anyone else sees the work of the projects they have a role in.
 *
 * Where each part comes from:
 *  - the heatmap and model spend: the usage ledger (raw usage records), by the team's `teamId` for a team and by the
 *    member's `memberId` (workbench D401) for a member or a specialist; a team member renamed is found under its former
 *    names too. Calls are counted in the organisation's time zone, Monday first;
 *  - task completion, open tasks and cost per completed task: the team's board (`board` = the team's id) in every project
 *    the caller sees, a member's by its assignee; cost per completed task joins the ledger by `taskId`;
 *  - run success: the runs machines report (agentRuns.ts), whose end says how each ended (`agentRunOutcome`);
 *  - waiting for you: the caller's Inbox records about the team, and the team's tasks that are blocked or wait for review.
 *
 * Nothing is made up: a part with no data behind it says so (`reported: false`) and the console words it ("Not reported
 * yet by this team's machines"), never as a zero. Money is in US dollars, as the ledger keeps it; the console converts.
 * `spend.usd` is metered spend only (costs.ts: a call on a paid seat is `subscriptionUsd`, its list-price value, not spend).
 */
import { z } from "zod";
export const MEMBER_OVERVIEW_SCHEMA = 1;
export const MEMBER_OVERVIEW_RANGES = ["1h", "1d", "7d", "30d", "60d", "90d"];
export const MEMBER_OVERVIEW_DEFAULT_RANGE = "30d";
const HOUR = 3_600_000;
const DAY = 24 * HOUR;
export const MEMBER_OVERVIEW_RANGE_MS = { "1h": HOUR, "1d": DAY, "7d": 7 * DAY, "30d": 30 * DAY, "60d": 60 * DAY, "90d": 90 * DAY };
/** The longest range of one's own: the raw usage records are kept 400 days by default. */
export const MEMBER_OVERVIEW_MAX_DAYS = 366;
/** The run-success objective: 95 per cent of runs succeed over a trailing 30 days. Fixed today; answered so it can move. */
export const RUN_SUCCESS_TARGET = 0.95;
export const RUN_SUCCESS_WINDOW_DAYS = 30;
/** How many waiting items the answer lists (the newest are dropped first; `waiting.total` counts them all). */
export const MEMBER_OVERVIEW_WAITING_MAX = 12;
/** The period asked for: a range by its key, or `since` and `until` of one's own. An error in words when it makes no sense. */
export function memberOverviewRange(q, now) {
    if (q.since !== undefined || q.until !== undefined) {
        const since = typeof q.since === "string" ? new Date(q.since) : undefined;
        const until = typeof q.until === "string" ? new Date(q.until) : now;
        if (!since || Number.isNaN(since.getTime()) || Number.isNaN(until.getTime()))
            return { error: "since and until are dates and times (ISO 8601)" };
        if (until.getTime() <= since.getTime())
            return { error: "until comes after since" };
        if (until.getTime() - since.getTime() > MEMBER_OVERVIEW_MAX_DAYS * DAY)
            return { error: `a range is at most ${MEMBER_OVERVIEW_MAX_DAYS} days` };
        return { key: "custom", since: since.toISOString(), until: until.toISOString() };
    }
    const key = q.range === undefined || q.range === "" ? MEMBER_OVERVIEW_DEFAULT_RANGE : q.range;
    if (typeof key !== "string" || !MEMBER_OVERVIEW_RANGES.includes(key))
        return { error: `range is one of ${MEMBER_OVERVIEW_RANGES.join(", ")}` };
    const k = key;
    return { key: k, since: new Date(now.getTime() - MEMBER_OVERVIEW_RANGE_MS[k]).toISOString(), until: now.toISOString() };
}
const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const formatters = new Map();
/** The weekday (0 Monday … 6 Sunday) and hour (0–23) a time falls on in a time zone (IANA). */
export function weekHourOf(at, timeZone) {
    let f = formatters.get(timeZone);
    if (!f) {
        f = new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short", hour: "2-digit", hourCycle: "h23" });
        if (formatters.size > 50)
            formatters.clear();
        formatters.set(timeZone, f);
    }
    let day = 0;
    let hour = 0;
    for (const p of f.formatToParts(at)) {
        if (p.type === "weekday")
            day = Math.max(0, weekdays.indexOf(p.value));
        else if (p.type === "hour")
            hour = Number(p.value) % 24;
    }
    return { day, hour };
}
/**
 * The error budget over a window: the failures the target allows for the runs it saw, how many are left, and the share
 * of the budget left (0 to 1). With no runs there is no budget to speak of: `left` is 1 and nothing is allowed yet.
 */
export function errorBudget(runs, failed, target = RUN_SUCCESS_TARGET) {
    const allowed = Math.max(0, runs * (1 - target));
    if (allowed <= 0)
        return { allowed: 0, left: failed > 0 ? 0 : 1, remaining: 0 };
    const left = Math.max(0, 1 - failed / allowed);
    return { allowed: Math.round(allowed * 10) / 10, left: Math.round(left * 1000) / 1000, remaining: Math.max(0, Math.floor(allowed - failed)) };
}
/** The median of some numbers; undefined for none. */
export function overviewMedian(xs) {
    if (!xs.length)
        return undefined;
    const s = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
/** A member's face, as the team record draws it (orgTeamsV2.ts): its character and colour, or a picture. */
export const overviewFaceSchema = z.object({
    name: z.string(),
    human: z.boolean().optional(),
    avatar: z.number().int().optional(),
    color: z.string().optional(),
    useCharacter: z.boolean().optional(),
    pictureUrl: z.string().optional(),
});
export const memberOverviewMemberSchema = overviewFaceSchema.extend({
    /** The id usage and runs carry for it: `agent:<teamId>/<name>`; absent for a person. */
    memberId: z.string().optional(),
    /** The member's own id (orgTeamsV2.ts `id`), when the workbench gave one. */
    id: z.string().optional(),
    role: z.string().optional(),
    /** It leads the team: the workbench's `orchestration.opsManager` (the team's `lead`). */
    planner: z.boolean().optional(),
    /** The specialist it is, by its record's id. */
    ref: z.string().optional(),
});
export const memberOverviewSubjectSchema = z.object({
    kind: z.enum(["team", "member", "specialist"]),
    teamId: z.string(),
    /** The team's or the specialist's name, or the member's. */
    name: z.string(),
    /** A member's team, by name. */
    teamName: z.string().optional(),
    memberName: z.string().optional(),
    /** The ids usage and runs are read under: the member's, with its former names' (empty for a team). */
    memberIds: z.array(z.string()),
    role: z.string().optional(),
    version: z.number().int(),
    /** What it owns and what it must not decide: the record's description, else the start of its instructions. */
    scope: z.object({ text: z.string(), from: z.enum(["description", "instructions"]) }).optional(),
    /** Its skills, catalogue and free-text, by name: the tags on an agent's card. */
    tags: z.array(z.string()),
    face: overviewFaceSchema.optional(),
    /** A team's members in its order. */
    members: z.array(memberOverviewMemberSchema).optional(),
    /** The caller may change the record (its owner, an administrator or a portfolio manager). */
    mayEdit: z.boolean(),
});
const sums = {
    /** Metered spend, US dollars. */
    usd: z.number(),
    /** The list-price value of calls on paid seats: not spend. */
    subscriptionUsd: z.number(),
    tokens: z.number(),
    calls: z.number(),
    /** Calls with no known price: in `calls` and `tokens`, in no money figure. */
    unpricedCalls: z.number(),
};
export const memberOverviewSchema = z.object({
    schema: z.literal(MEMBER_OVERVIEW_SCHEMA),
    subject: memberOverviewSubjectSchema,
    range: z.object({ key: z.string(), since: z.string(), until: z.string(), timeZone: z.string() }),
    /** Model calls by weekday (0 Monday) and hour, in the organisation's time zone: `cells[day][hour]`. */
    heatmap: z.object({ reported: z.boolean(), calls: z.number(), max: z.number(), cells: z.array(z.array(z.number())) }),
    tiles: z.object({
        /** Tasks finished of those worked in the range (a team: done against closed otherwise). */
        completion: z.object({ reported: z.boolean(), done: z.number(), total: z.number(), notDone: z.number(), rate: z.number().nullable() }),
        /** Open now: being worked, waiting their turn, waiting on a person. */
        open: z.object({ active: z.number(), queued: z.number(), waiting: z.number() }),
        /** A team: runs going now, open tasks on its board, tasks in review. */
        activeWork: z.object({ running: z.number(), onBoard: z.number(), inReview: z.number() }).optional(),
        spend: z.object({ reported: z.boolean(), ...sums }),
        /** The median spend of a completed task in the range, from the ledger by task. */
        costPerTask: z.object({ reported: z.boolean(), medianUsd: z.number().nullable(), tasks: z.number() }),
        /** A team: the budget that applies to it nearest its limit, from the budgets' stored standing. */
        nearestLimit: z
            .object({ budgetId: z.string().optional(), name: z.string().optional(), scope: z.string().optional(), percent: z.number().nullable(), state: z.string().optional(), unit: z.enum(["usd", "tokens"]).optional() })
            .optional(),
    }),
    runSuccess: z.object({
        reported: z.boolean(),
        target: z.number(),
        windowDays: z.number(),
        since: z.string(),
        /** Runs that ended in the window, cancelled ones left out (the workbench's rule): succeeded, failed or handed back. */
        runs: z.number(),
        succeeded: z.number(),
        failed: z.number(),
        cancelled: z.number(),
        handedBack: z.number(),
        /** Succeeded of `runs` (a run handed back is not a success; a cancelled one is not counted); null with none. */
        rate: z.number().nullable(),
        allowedFailures: z.number(),
        /** Share of the error budget left, 0 to 1. */
        budgetLeft: z.number(),
        /** Failures (failed or handed back) the budget still allows at this volume of runs. */
        failuresLeft: z.number(),
        /** The budget left at the end of each day of the window, oldest first (the day in the organisation's time zone). */
        series: z.array(z.object({ day: z.string(), budgetLeft: z.number() })),
        /** Each failed run: when, its day in the organisation's time zone (as `series`), and the budget left after it. */
        failures: z.array(z.object({ at: z.string(), day: z.string(), budgetLeft: z.number(), runId: z.string() })),
    }),
    waiting: z.object({
        total: z.number(),
        items: z.array(z.object({
            id: z.string(),
            kind: z.enum(["approval", "question", "decision", "blocked", "review", "other"]),
            title: z.string(),
            since: z.string(),
            projectId: z.string(),
            projectName: z.string().optional(),
            /** The member it is about, by name, when known. */
            member: z.string().optional(),
            taskId: z.string().optional(),
            rootId: z.string().optional(),
            notificationId: z.string().optional(),
        })),
    }),
    /** Things the numbers leave out, in words. */
    notes: z.array(z.string()),
});
//# sourceMappingURL=memberOverview.js.map